import YootMesh from "../meshes/YootMesh"
import { Center, Float, Text3D } from "@react-three/drei"
import YootRhino from "../meshes/YootRhino"
import SparkleYutShader from "../shader/sparkleYut/SparkleYutShader"
import { useSpring, animated } from "@react-spring/three"
import { useEffect, useRef, useState } from "react"
import YootMeshUnrotated from "../meshes/YutMeshUnrotated"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { pauseGameAtom, showBonusAtom, yootAnimationPlayingAtom, languageAtom, timerAtom, turnExpireTimeAtom, turnStartTimeAtom } from "../GlobalState"
import { useFrame } from "@react-three/fiber"
import { useAnimationPlaying } from "../hooks/useAnimationPlaying"
import { getSocket } from "../socket.js"

const socket = getSocket();
import { useParams } from "wouter"
import translations from "../dictionaries/translations"
import MeshColors from "./MeshColors.jsx"

export default function YutBonus({ position, scale, rotation=[0,0,0], alwaysShow=false, enableClick=true, modelOnly=false, timerFade=false }) {
  
  const [showBonus, setShowBonus] = useAtom(showBonusAtom)
  const paused = useAtomValue(pauseGameAtom)
  const animationPlaying = useAnimationPlaying()
  const setYootAnimationPlaying = useSetAtom(yootAnimationPlayingAtom)
  const params = useParams()
  const language = useAtomValue(languageAtom)
  const timer = useAtomValue(timerAtom)
  const turnExpireTime = useAtomValue(turnExpireTimeAtom)
  const turnStartTime = useAtomValue(turnStartTimeAtom)
  const labelMaterial = useRef()
  const yutSkinGroup = useRef()
  const yutSkinMaterials = useRef([])

  useEffect(() => {
    if (!timerFade || !yutSkinGroup.current) return undefined
    const materials = []
    yutSkinGroup.current.traverse((object) => {
      if (!object.isMesh || object.userData.timerDepthPrepass || !object.material) return
      const sourceMaterials = Array.isArray(object.material) ? object.material : [object.material]
      const clonedMaterials = sourceMaterials.map((sourceMaterial) => {
        const material = sourceMaterial.clone()
        material.alphaHash = true
        return material
      })
      object.material = Array.isArray(object.material) ? clonedMaterials : clonedMaterials[0]
      materials.push(...clonedMaterials)
    })
    yutSkinMaterials.current = materials
    return () => materials.forEach((material) => material.dispose())
  }, [timerFade])

  useEffect(() => {
    if (!timerFade) return
    if (labelMaterial.current) {
      labelMaterial.current.opacity = 1
    }
    for (const material of yutSkinMaterials.current) {
      material.opacity = 1
    }
  }, [timerFade, turnExpireTime])

  useFrame((state) => {
    if (!labelMaterial.current) return
    let opacity = 1
    if (timerFade && timer && !paused && turnExpireTime && Date.now() >= turnStartTime) {
      const secondsRemaining = Math.max(0, (turnExpireTime - Date.now()) / 1000)
      opacity = secondsRemaining > 10
        ? 1
        : secondsRemaining > 5
        ? 0.1 + (secondsRemaining - 5) / 5 * 0.9
        : 0.5 + Math.sin(state.clock.elapsedTime * Math.PI * 3) * 0.5
    }
    labelMaterial.current.opacity = opacity
    for (const material of yutSkinMaterials.current) {
      material.opacity = opacity
    }
  })

  const { yutBonusScale } = useSpring({
    yutBonusScale: (alwaysShow || (showBonus && !animationPlaying)) ? 0.9 : 0,
  })

  const yutSprings = useSpring({
    from: {
      yut0Position: [0, 0.2, 0],
      yut0Rotation: [Math.PI/16, Math.PI/32, -Math.PI/8],
      // yut0Rotation: quaternion0,
      yut1Position: [0.4, 0, -0.2],
      yut1Rotation: [Math.PI/2+Math.PI/16, Math.PI/2+Math.PI/8, Math.PI/16],
      yut2Position: [0.8, 0.2, -0.1],
      yut2Rotation: [Math.PI/2+Math.PI/8, Math.PI/2-Math.PI/8, Math.PI/16],
      yut3Position: [1.1, -0.3, -0.1],
      yut3Rotation: [Math.PI/2+Math.PI/8, Math.PI/2-Math.PI/4, Math.PI/8],
    },
    to: [
      {
        yut0Position: [0.1, 0, 0],
        yut0Rotation: [0, 0, -Math.PI/4],
        // yut0Rotation: quaternion1,
        yut1Position: [0.5, 0, -0.3],
        yut1Rotation: [Math.PI/2+Math.PI/8, Math.PI/2+Math.PI/8,  Math.PI/16],
        yut2Position: [0.6, 0.3, 0],
        yut2Rotation: [Math.PI/2+Math.PI/8, Math.PI * 5 / 6, 0],
        yut3Position: [0.7, -0.2, -0.15],
        yut3Rotation: [Math.PI/2+Math.PI/16, Math.PI/2-Math.PI/12, Math.PI/8],
      },
      // Return to 0 (first position)
      {
        yut0Position: [0, 0.2, 0],
        yut0Rotation: [Math.PI/16, Math.PI/32, -Math.PI/8],
        // yut0Rotation: quaternion0,
        yut1Position: [0.4, 0, -0.2],
        yut1Rotation: [Math.PI/2+Math.PI/16, Math.PI/2+Math.PI/8, Math.PI/16],
        yut2Position: [0.8, 0.2, -0.1],
        yut2Rotation: [Math.PI/2+Math.PI/8, Math.PI/2-Math.PI/8, Math.PI/16],
        yut3Position: [1.1, -0.3, -0.1],
        yut3Rotation: [Math.PI/2+Math.PI/8, Math.PI/2-Math.PI/4, Math.PI/8],
      },
    ],
    loop: true,
    reset: true // Need this to loop
  })

  function Label() {
    
    const [hover, setHover] = useState(false)
    function handlePointerEnter(e) {
      e.stopPropagation()
      setHover(true)
      document.body.style.cursor = 'pointer'
    }
    function handlePointerLeave(e) {
      e.stopPropagation()
      setHover(false)
      document.body.style.cursor = 'default'
    }
    function handlePointerUp(e) {
      if (enableClick && !paused) {
        e.stopPropagation()
        document.body.style.cursor = 'default'
        if (alwaysShow || showBonus) {
          setYootAnimationPlaying(true)
          socket.emit('throwYut', {
            roomId: params.id.toUpperCase()
          })
          setShowBonus(false)
        }

        // const audio = new Audio('sounds/effects/yut-bonus.mp3');
        // audio.volume = 1;
        // audio.play();
      }
    }

    return <group>
      <group name='label' position={[0.55, 0.5, 0.8]}> 
        <mesh scale={[0.9, 0.01, 0.3]}>
          <cylinderGeometry args={[1, 1, 1]}/>
          <meshStandardMaterial color={ hover ? 'blue' : showBonus ? MeshColors.explainBlue : '#EE9E26' }/>
        </mesh>
        <mesh scale={[0.85, 0.02, 0.25]}>
          <cylinderGeometry args={[1, 1, 1]}/>
          <meshStandardMaterial color='black'/>
        </mesh>
        <Center position={[0, 0.025, 0]}>
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            rotation={[-Math.PI/2, 0, 0]}
            size={0.24}
            height={0.01}
            renderOrder={10}
          >
            { showBonus ? translations.game.bonus[language] : translations.game.throw[language] }
            <meshStandardMaterial ref={labelMaterial} color={ hover ? 'blue' : showBonus ? MeshColors.explainBlue : '#EE9E26' } transparent depthTest={false} depthWrite={false}/>
          </Text3D>
        </Center>
      </group>
      <mesh 
        name='wrapper'
        position={[0.5, 0, 0]} 
        onPointerEnter={e=>handlePointerEnter(e)} 
        onPointerLeave={e=>handlePointerLeave(e)} 
        onPointerUp={e=>handlePointerUp(e)}
      >
        <sphereGeometry args={[1, 32, 16]}/>
        <meshStandardMaterial color='white' transparent opacity={0}/>
      </mesh>
    </group>
  }

  return <animated.group name='yut-bonus-animation-wrapper' scale={yutBonusScale} position={position} rotation={rotation}>
    <Float rotationIntensity={0.2} speed={7} floatIntensity={3} floatingRange={[-0.1, 0.1]}>
      <group name='yut-bonus' scale={scale}>
        <group ref={yutSkinGroup}>
          <YootMeshUnrotated scale={0.2} position={yutSprings.yut0Position} rotation={yutSprings.yut0Rotation} timerFade={timerFade}/>
          <YootMesh scale={0.2} position={yutSprings.yut1Position} rotation={yutSprings.yut1Rotation} timerFade={timerFade}/>
          <YootMesh scale={0.2} position={yutSprings.yut2Position} rotation={yutSprings.yut2Rotation} timerFade={timerFade}/>
          <YootRhino scale={0.2} position={yutSprings.yut3Position} rotation={yutSprings.yut3Rotation} timerFade={timerFade}/>
        </group>
        {!modelOnly && showBonus && <SparkleYutShader texturePath={'/textures/particles/8.png'}/>}
        {!modelOnly && <Label/>}
      </group>
    </Float>
  </animated.group>
}
